import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/services/api'
import type { Case, CaseSettingsUpdate } from '@/services/api/cases'
import { casesApi } from '@/services/api/cases'
import { graphApi } from '@/services/api/graph'
import { schemasApi } from '@/services/api/schemas'

// --- Query Keys ---
// Centralize keys so any component can invalidate the right cache.

export const caseKeys = {
  all: ['cases'] as const,
  list: ['cases', 'list'] as const,
  detail: (caseId: string) => ['case', caseId] as const,
  settings: (caseId: string) => ['case', caseId, 'settings'] as const,
  extractionRuns: (caseId: string) => ['case', caseId, 'extraction-runs'] as const,
  knowledgeGraph: (caseId: string, entityTypes?: string[]) =>
    ['case', caseId, 'knowledge-graph', entityTypes ?? []] as const,
  allData: (caseId: string, params: Record<string, unknown>) =>
    ['case', caseId, 'all-data', params] as const,
  documentDetail: (caseId: string, documentId: string) =>
    ['case', caseId, 'document', documentId, 'detail'] as const,
  documentInternal: (caseId: string, documentId: string) =>
    ['case', caseId, 'document', documentId, 'internal'] as const,
  graphEntityDetail: (caseId: string, entityId: string) =>
    ['case', caseId, 'graph', 'entity', entityId, 'detail'] as const,
  graphRelationshipDetail: (caseId: string, relationshipId: string) =>
    ['case', caseId, 'graph', 'relationship', relationshipId, 'detail'] as const,
  // Case Summary card on overview. ``summaryStatus`` is the cheap
  // pre-flight, ``summary`` fetches the full cached payload (404 when
  // none has been generated — the card maps that to the CTA state).
  summaryStatus: (caseId: string) => ['case', caseId, 'summary', 'status'] as const,
  summary: (caseId: string) => ['case', caseId, 'summary'] as const,
  // Full schema (entity + relation types + patterns) bound to a case.
  // Keyed by ``schemaId`` because the underlying ``/schemas/{id}`` payload
  // is identical across cases that share a schema — sharing the cache
  // entry across cases avoids redundant fetches.
  schema: (schemaId: string) => ['extraction-schema', schemaId] as const,
  activeSchema: () => ['extraction-schema', 'active'] as const,
}

// --- Queries ---

export function useCaseSettings(caseId: string) {
  return useQuery({
    queryKey: caseKeys.settings(caseId),
    queryFn: () => casesApi.getCaseSettings(caseId),
    enabled: !!caseId,
  })
}

/**
 * Resolve the full schema (entity_types, relationship_types, patterns) for a
 * case. Reads case settings to find the case-bound ``schema_id``; falls back
 * to the globally active schema when the case does not pin one.
 *
 * The dialog that creates new graph relationships uses this so the relation
 * dropdown reflects the user's chosen schema instead of a hardcoded list.
 */
export function useCaseSchema(caseId: string) {
  const settingsQ = useCaseSettings(caseId)
  const schemaId = settingsQ.data?.schema_id ?? null

  // Fetch the case-pinned schema when present.
  const pinnedQ = useQuery({
    queryKey: schemaId ? caseKeys.schema(schemaId) : ['extraction-schema', 'noop'],
    queryFn: () => schemasApi.get(schemaId as string),
    enabled: Boolean(schemaId),
    staleTime: 60_000,
  })

  // Otherwise (or when the pinned fetch failed), fall back to the active
  // schema. ``enabled`` flips only once we know the settings finished
  // loading AND there is no pinned schema — keeps a single in-flight call.
  const activeQ = useQuery({
    queryKey: caseKeys.activeSchema(),
    queryFn: () => schemasApi.getActive(),
    enabled: settingsQ.isSuccess && !schemaId,
    staleTime: 60_000,
    retry: false, // 404 when no active schema is configured globally
  })

  const data = pinnedQ.data ?? activeQ.data ?? null
  const isLoading =
    settingsQ.isLoading ||
    (schemaId ? pinnedQ.isLoading : !settingsQ.isSuccess || activeQ.isLoading)
  const error = pinnedQ.error ?? activeQ.error ?? settingsQ.error ?? null

  return { data, isLoading, error }
}

// Cheap status fetch the overview card uses to pick the empty-state CTA.
// Stays fresh-on-mount so toggling "Add to report" on a separate page
// flips the eligible count when the user navigates back.
export function useCaseSummaryStatus(caseId: string) {
  return useQuery({
    queryKey: caseKeys.summaryStatus(caseId),
    queryFn: () => casesApi.getCaseSummaryStatus(caseId),
    staleTime: 0,
  })
}

// Fetches the cached summary payload. A 404 from the backend means
// "never generated" — react-query gives us the error and the card maps it
// to the Generate CTA state. ``enabled`` lets the card defer the fetch
// until the status check confirms a summary exists, avoiding a guaranteed
// 404 + retry on cases that have no summary yet.
export function useCaseSummary(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: caseKeys.summary(caseId),
    queryFn: () => casesApi.getCaseSummary(caseId),
    enabled,
    retry: false,
    staleTime: Infinity,
  })
}

// --- Mutations ---

export function useGenerateCaseSummary(caseId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (locale: string = 'en') => casesApi.generateCaseSummary(caseId, locale),
    onSuccess: envelope => {
      // Seed both caches so the card swaps state without a second roundtrip.
      queryClient.setQueryData(caseKeys.summary(caseId), envelope)
      queryClient.setQueryData(caseKeys.summaryStatus(caseId), {
        has_summary: true,
        generated_at: envelope.generated_at,
        // Eligible count needs a refetch to be authoritative; invalidate
        // and let the next mount pull the truth.
        eligible_investigation_count: 0,
      })
      queryClient.invalidateQueries({ queryKey: caseKeys.summaryStatus(caseId) })
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to generate case summary')
    },
  })
}

export function useUpdateCaseSettings(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (update: CaseSettingsUpdate) => casesApi.updateCaseSettings(caseId, update),
    onSuccess: updatedSettings => {
      // Replace the cached settings with the server response
      queryClient.setQueryData(caseKeys.settings(caseId), updatedSettings)
      // Settings affect what entities are visible — invalidate all entity data views
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'all-data'] })
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'knowledge-graph'] })
    },
    onError: () => {
      toast.error('Failed to update settings')
    },
  })
}

export function useResetCase(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => casesApi.resetCase(caseId),
    onSuccess: () => {
      // After reset, case stats change — invalidate case detail + settings
      queryClient.invalidateQueries({ queryKey: caseKeys.detail(caseId) })
      queryClient.invalidateQueries({ queryKey: caseKeys.settings(caseId) })
      // Reset wipes entities/relationships — cases list shows those counts
      queryClient.invalidateQueries({ queryKey: caseKeys.list })
      queryClient.invalidateQueries({ queryKey: caseKeys.knowledgeGraph(caseId) })
      queryClient.invalidateQueries({ queryKey: ['case', caseId, 'all-data'] })
    },
  })
}

export function useUpdateCaseDescription(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (description: string) => api.cases.update(caseId, { description }),
    // Merge the saved fields only, so every editor closes on the new description at
    // once; PATCH reports no workflows, which the detail's GET does.
    onSuccess: updated => {
      queryClient.setQueryData<Case>(
        caseKeys.detail(caseId),
        prev =>
          prev && { ...prev, description: updated.description, updated_at: updated.updated_at }
      )
    },
    onError: () => {
      toast.error('Failed to update description')
    },
  })
}

export function useUpdateCaseStatus(caseId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (status: string) => api.cases.update(caseId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: caseKeys.detail(caseId) })
      queryClient.invalidateQueries({ queryKey: caseKeys.list })
    },
    onError: () => {
      toast.error('Failed to update status')
    },
  })
}

export function useCaseDocumentDetail(caseId: string, documentId: string | null | undefined) {
  return useQuery({
    queryKey: caseKeys.documentDetail(caseId, documentId ?? ''),
    queryFn: () => casesApi.getCaseDocumentDetail(caseId, documentId as string),
    enabled: Boolean(caseId && documentId),
  })
}

export function useCaseDocumentInternal(
  caseId: string,
  documentId: string | null | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: caseKeys.documentInternal(caseId, documentId ?? ''),
    queryFn: () => casesApi.getCaseDocumentInternal(caseId, documentId as string),
    enabled: enabled && Boolean(caseId && documentId),
  })
}

/** Lazy-loaded full entity inspector payload — fired only when the analyst
 *  expands a specific entity row inside the DocumentDetailOverlay. */
export function useCaseGraphEntityDetail(
  caseId: string,
  entityId: string | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: caseKeys.graphEntityDetail(caseId, entityId ?? ''),
    queryFn: () => casesApi.getCaseGraphEntity(caseId, entityId as string),
    enabled: enabled && Boolean(caseId && entityId),
    staleTime: 5 * 60 * 1000,
  })
}

/** Lazy-loaded relationship payload — fired when the analyst expands a
 *  relationship row inside the DocumentDetailOverlay. */
export function useCaseGraphRelationshipDetail(
  caseId: string,
  relationshipId: string | null | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: caseKeys.graphRelationshipDetail(caseId, relationshipId ?? ''),
    queryFn: () => casesApi.getCaseGraphRelationship(caseId, relationshipId as string),
    enabled: enabled && Boolean(caseId && relationshipId),
    staleTime: 5 * 60 * 1000,
  })
}

export function useKnowledgeGraph(caseId: string, entityTypeFilter: Set<string> = new Set()) {
  const entityTypes = entityTypeFilter.size > 0 ? [...entityTypeFilter] : undefined
  return useQuery({
    queryKey: caseKeys.knowledgeGraph(caseId, entityTypes),
    queryFn: () => graphApi.getKnowledgeGraph(caseId, { limit: 500, entityTypes }),
  })
}

export function useAllData(
  caseId: string,
  params: {
    skip: number
    limit: number
    search?: string
    entityType?: string
    sortField?: string
    sortDir?: string
    isSuspect?: boolean | null
    minConnections?: number
    nodeIds?: string[]
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: caseKeys.allData(caseId, params),
    queryFn: () => graphApi.getAllData(caseId, params),
    placeholderData: prev => prev, // keep previous data while fetching next page
    enabled: options?.enabled ?? true,
  })
}
